import type { CalEvent, WorkingHours } from "../types";
import { declineChange, moveModeFor, proposeChange, rescheduleChange, type CalendarChange } from "./changes";
import { displaySubject, type Clash } from "./day";
import { nextFree, type FreeSlot, type Schedule } from "./freeBusy";
import { dayKeyOf, dayLabel, fd, hm, minutesOfDay } from "./time";

/* The clash band's options, ported from the mockup's resolveOpts(). */

export interface ResolveOption {
  readonly key: "A" | "B" | "C";
  readonly title: string;
  readonly detail: string;
  readonly change: CalendarChange;
  /** Switch the day view here after choosing (the meeting moved to another day). */
  readonly navigateTo?: string;
}

/** Shortest meeting a trim or shorten may leave. */
const MIN_REMAINING_MIN = 15;

const minutes = (a: Date, b: Date) => Math.round((b.getTime() - a.getTime()) / 60000);
const firstName = (name: string) => name.trim().split(/\s+/)[0] || name;
const hasAttendees = (e: CalEvent) => e.attendees.some((a) => a.type !== "resource");
/** " · attendees will be updated" for a meeting with people on it. */
const updateNote = (e: CalEvent) => (hasAttendees(e) ? " · attendees will be updated" : "");

/** The people whose calendars matter when moving `e`: everyone invited who hasn't declined, and the organiser. */
export function peopleToCheck(e: CalEvent, selfEmails: ReadonlySet<string>): string[] {
  const out = new Set<string>();
  for (const a of e.attendees) {
    if (a.type !== "resource" && a.response !== "no" && !selfEmails.has(a.email)) out.add(a.email);
  }
  if (!e.isOrganizer && e.organizerEmail && !selfEmails.has(e.organizerEmail)) out.add(e.organizerEmail);
  return [...out];
}

/** Whether a slot falls outside working hours: before they start, after they end, or on a day off. */
function outsideWorkingHours(slot: { readonly start: Date; readonly end: Date }, hours: WorkingHours): boolean {
  return (
    !hours.days.has(slot.start.getDay()) ||
    minutesOfDay(slot.start) < hours.startMin ||
    dayKeyOf(slot.end) !== dayKeyOf(slot.start) ||
    minutesOfDay(slot.end) > hours.endMin
  );
}

export interface ResolveContext {
  /** Your events, with your pending changes, from the clash's day up to `knownUntil`. */
  readonly events: readonly CalEvent[];
  /** Your calendar and everyone's availability are known up to here, so option A offers nothing later. */
  readonly knownUntil: Date;
  readonly schedules: ReadonlyMap<string, Schedule>;
  readonly hours: WorkingHours;
  readonly now: Date;
  /** Every address of your mailbox (see Self). */
  readonly selfEmails: ReadonlySet<string>;
}

/** Option A's availability line: who is known to be free at `slot`, and whether it's outside working hours. */
function availabilityNote(people: readonly string[], slot: FreeSlot, hours: WorkingHours): string {
  let availability = "You and everyone invited are free";
  if (people.length === 0) availability = "You're free then";
  else if (slot.unknown.length) availability = `You're free then · availability unknown for ${slot.unknown.length} of ${people.length}`;
  if (outsideWorkingHours(slot, hours)) availability += " · outside your working hours";
  return availability;
}

/**
 * A: move the meeting you own (else the later one) to the next time everyone is free: later the
 * same day if anything fits (even outside working hours), else the next working days.
 */
function moveOption(a: CalEvent, b: CalEvent, ctx: ResolveContext): Omit<ResolveOption, "key"> | null {
  const mv = a.isOrganizer && !b.isOrganizer ? a : b;
  const other = mv === a ? b : a;
  const mode = moveModeFor(mv);
  if (mode === "none") return null;
  const people = peopleToCheck(mv, ctx.selfEmails);
  const fromDay = dayKeyOf(mv.start);
  // Nothing later that day can be free when the other meeting runs past midnight.
  const otherEndsSameDay = dayKeyOf(other.end) === fromDay;
  const slot = nextFree({
    events: ctx.events,
    moving: mv,
    people,
    schedules: ctx.schedules,
    durationMin: minutes(mv.start, mv.end),
    fromDay,
    fromMin: otherEndsSameDay ? minutesOfDay(other.end) : 0,
    hours: ctx.hours,
    now: ctx.now,
    firstDayAnyTime: otherEndsSameDay,
  });
  if (!slot || slot.end > ctx.knownUntil) return null;
  const slotDay = dayKeyOf(slot.start);
  const at = hm(minutesOfDay(slot.start));
  const where = slotDay === fromDay ? at : `${dayLabel(slotDay)}, ${at}`;
  const availability = availabilityNote(people, slot, ctx.hours);
  const reschedule = mode === "reschedule";
  return {
    title: `Move ${displaySubject(mv)} to ${where}`,
    detail: reschedule
      ? `${availability}${updateNote(mv)}`
      : `${availability} · proposed to ${firstName(mv.organizerName)} · you'll be marked tentative`,
    change: reschedule ? rescheduleChange(mv, slot.start, slot.end, "move") : proposeChange(mv, slot.start, slot.end),
    ...(slotDay !== fromDay ? { navigateTo: slotDay } : {}),
  };
}

/** B: trim the later meeting's start, or shorten the earlier one — only for a meeting you own. */
function trimOption(a: CalEvent, b: CalEvent): Omit<ResolveOption, "key"> | null {
  if (b.isOrganizer && minutes(a.end, b.end) >= MIN_REMAINING_MIN) {
    const overlap = minutes(b.start, a.end < b.end ? a.end : b.end);
    const remaining = minutes(a.end, b.end);
    return {
      title: `Start ${displaySubject(b)} at ${hm(minutesOfDay(a.end))} (${fd(remaining)})`,
      detail: `Clears the ${fd(overlap)} overlap and keeps the same end time${updateNote(b)}`,
      change: rescheduleChange(b, a.end, b.end, "trim"),
    };
  }
  if (a.isOrganizer && minutes(a.start, b.start) >= MIN_REMAINING_MIN) {
    const remaining = minutes(a.start, b.start);
    return {
      title: `Shorten ${displaySubject(a)} to ${fd(remaining)}`,
      detail: `Ends as the next meeting begins${updateNote(a)}`,
      change: rescheduleChange(a, a.start, b.start, "shorten"),
    };
  }
  return null;
}

/** C: decline the one you don't own, preferring a meeting where you're only optional. */
function declineOption(a: CalEvent, b: CalEvent, selfEmails: ReadonlySet<string>): Omit<ResolveOption, "key"> | null {
  const optional = (e: CalEvent) => e.attendees.some((x) => selfEmails.has(x.email) && x.type === "optional");
  const declinable = [a, b].filter((e) => !e.isOrganizer && !e.isCancelled).sort((x, y) => Number(optional(y)) - Number(optional(x)));
  const dc = declinable[0];
  if (!dc) return null;
  const told = dc.responseRequested ? `${firstName(dc.organizerName)} will be notified` : "no response is sent";
  return {
    title: `Decline ${displaySubject(dc)}`,
    detail: `${optional(dc) ? "You are optional · " : ""}${told}`,
    change: declineChange(dc),
  };
}

export function resolveOptions(clash: Clash, ctx: ResolveContext): ResolveOption[] {
  const { a, b } = clash;
  const options = [moveOption(a, b, ctx), trimOption(a, b), declineOption(a, b, ctx.selfEmails)].filter((o) => o !== null);
  const keys = ["A", "B", "C"] as const;
  return options.map((o, i) => ({ ...o, key: keys[i] ?? "C" }));
}
